import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { HygieneStaffReport, HygieneStaftReportService } from '../../hygiene-staft-report.service';
import flatpickr from 'flatpickr';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TicketImageDialogComponent } from '../ticket-image-dialog/ticket-image-dialog.component';
import { TicketingService, TicketWeb } from '../../ticketing.service';

@Component({
  selector: 'app-ticket-reports',
  templateUrl: './ticket-reports.component.html',
  styleUrls: ['./ticket-reports.component.scss']
})
export class TicketReportsComponent {
  mapIconPath = 'assets/image/college-map-icon.svg';
  activeDateTab: 'today' | 'yesterday' | 'last7' | 'month' | 'custom' = 'today';
  activeStatusTab: 'pending' | 'inProgress' | 'resolved' = 'pending';
  startDate: string = '';
  endDate: string = '';
  dateRangePickerInit = false;

  stateList: any[] = [];
  filteredStateList: any[] = [];
  selectedStateId: any = null;
  selectedStateName = '';
  stateQuery = '';

  zoneList: any[] = [];
  filteredZoneList: any[] = [];
  selectedZoneId: any = null;
  selectedZoneName: any = null;
  zoneQuery = '';

  cityList: any[] = [];
  filteredCityList: any[] = [];
  selectedCityId: any = null;
  selectedCityName = '';
  cityQuery = '';

  campusList: any[] = [];
  filteredCampusList: any[] = [];
  selectedCampusId: any = null;
  selectedCampusName = '';
  campusQuery = '';

  buildingList: any[] = [];
  filteredBuildingList: any[] = [];
  selectedBuildingId: any = null;
  selectedBuildingName = '';
  buildingQuery = '';

  stateOpen = false;
  zoneOpen = false;
  cityOpen = false;
  campusOpen = false;
  buildingOpen = false;

  showCompare = false;
  reportData: HygieneStaffReport | null = null;
  // Flat tickets from getticketdashboardforweb
  ticketDashboardTickets: TicketWeb[] = [];
  /** Track cards whose image URL failed, so we can hide broken img tag */
  imageErrorIds = new Set<string>();
  /** User-selected dates in Custom tab (preserved when switching tabs) */
  customStartDate = '';
  customEndDate = '';
  /** Open calendar automatically when popover first opens */
  openCalendarWhenReady = false;
  /** Custom Date popover open (Quick-Links style dropdown) */
  customDatePopoverOpen = false;
  /** Flatpickr instance for custom range (used to open calendar programmatically) */
  private flatpickrInstance: any = null;

  // Pagination for ticket cards (same structure as hygiene component)
  pageSize = 10;
  currentPage = 1;

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.ticketBuildingCards.length / this.pageSize));
  }

  get paginatedTicketCards() {
    const list = this.ticketBuildingCards;
    const start = (this.currentPage - 1) * this.pageSize;
    return list.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  // Aggregated counts by period
  ticketToday = { pending: 0, inProgress: 0, resolved: 0 };
  ticketYesterday = { pending: 0, inProgress: 0, resolved: 0 };
  ticketLast7 = { pending: 0, inProgress: 0, resolved: 0 };
  ticketThisMonth = { pending: 0, inProgress: 0, resolved: 0 };

  get ticketTotalCount(): number {
    const s = this.ticketActivePeriodSummary;
    return s.total;
  }

  /** Label for Custom Date trigger button (selected range or prompt). */
  get customDateTriggerLabel(): string {
    if (this.customStartDate && this.customEndDate) {
      return this.customStartDate === this.customEndDate
        ? this.customStartDate
        : `${this.customStartDate} – ${this.customEndDate}`;
    }
    return 'Select date range';
  }

  private parseDate(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  private isSameDay(d: Date, y: number, m: number, day: number): boolean {
    return d.getFullYear() === y && d.getMonth() === m && d.getDate() === day;
  }

  private withinLast7Days(date: Date, today: Date): boolean {
    const diffMs = today.getTime() - date.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays < 7;
  }

  /** Format a Date to local YYYY-MM-DD (same as calendar selection) for consistent range comparison */
  private formatDateToLocalYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** True if both custom start/end are set and dt (date part) is within [start, end] inclusive */
  private withinCustomRange(dt: Date): boolean {
    if (!this.customStartDate || !this.customEndDate) return false;
    const ticketDate = this.formatDateToLocalYMD(dt);
    return ticketDate >= this.customStartDate && ticketDate <= this.customEndDate;
  }

  private normalizeStatus(status: string | null | undefined): 'pending' | 'inProgress' | 'resolved' | null {
    const up = (status ?? '').toUpperCase();
    if (up === 'PENDING') return 'pending';
    if (up === 'INPROGRESS' || up === 'IN PROGRESS') return 'inProgress';
    if (up === 'RESOLVED') return 'resolved';
    return null;
  }

  private resetTicketSummaries(): void {
    this.ticketToday = { pending: 0, inProgress: 0, resolved: 0 };
    this.ticketYesterday = { pending: 0, inProgress: 0, resolved: 0 };
    this.ticketLast7 = { pending: 0, inProgress: 0, resolved: 0 };
    this.ticketThisMonth = { pending: 0, inProgress: 0, resolved: 0 };
  }

  private updateDashboardFromTickets(): void {
    this.resetTicketSummaries();
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();

    const yesterday = new Date(today);
    yesterday.setDate(d - 1);

    for (const t of this.ticketDashboardTickets) {
      const dt = this.parseDate(t.CREATED_ON);
      if (!dt) continue;
      const st = this.normalizeStatus(t.STATUS);
      if (!st) continue;

      // Today
      if (this.isSameDay(dt, y, m, d)) {
        this.ticketToday[st]++;
      }

      // Yesterday
      if (this.isSameDay(dt, yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())) {
        this.ticketYesterday[st]++;
      }

      // Last 7 days (including today)
      if (this.withinLast7Days(dt, today)) {
        this.ticketLast7[st]++;
      }

      // This month
      if (dt.getFullYear() === y && dt.getMonth() === m) {
        this.ticketThisMonth[st]++;
      }
    }
  }

  get ticketActivePeriodSummary(): { pending: number; inProgress: number; resolved: number; total: number } {
    let s = { pending: 0, inProgress: 0, resolved: 0 };
    if (this.activeDateTab === 'today') {
      s = this.ticketToday;
    } else if (this.activeDateTab === 'yesterday') {
      s = this.ticketYesterday;
    } else if (this.activeDateTab === 'last7') {
      s = this.ticketLast7;
    } else if (this.activeDateTab === 'month') {
      s = this.ticketThisMonth;
    } else if (this.activeDateTab === 'custom') {
      for (const t of this.ticketDashboardTickets) {
        const dt = this.parseDate(t.CREATED_ON);
        if (!dt || !this.withinCustomRange(dt)) continue;
        const st = this.normalizeStatus(t.STATUS);
        if (st) s[st]++;
      }
    }
    return { ...s, total: s.pending + s.inProgress + s.resolved };
  }

  get ticketTodayTotalCount(): number {
    const s = this.ticketToday;
    return s.pending + s.inProgress + s.resolved;
  }

  get ticketYesterdayTotalCount(): number {
    const s = this.ticketYesterday;
    return s.pending + s.inProgress + s.resolved;
  }

  get ticketLast7DaysTotalCount(): number {
    const s = this.ticketLast7;
    return s.pending + s.inProgress + s.resolved;
  }

  get ticketThisMonthTotalCount(): number {
    const s = this.ticketThisMonth;
    return s.pending + s.inProgress + s.resolved;
  }

  /** Total tickets in the selected custom date range (for Custom Date tab label, regardless of active tab). */
  get customDateRangeTotalCount(): number {
    if (!this.customStartDate || !this.customEndDate) return 0;
    let total = 0;
    for (const t of this.ticketDashboardTickets) {
      const dt = this.parseDate(t.CREATED_ON);
      if (!dt || !this.withinCustomRange(dt)) continue;
      const st = this.normalizeStatus(t.STATUS);
      if (st) total++;
    }
    return total;
  }

  /** Ticket count for the currently selected status tab (matches Pending / In Progress / Resolved number). */
  get ticketCountForCurrentStatus(): number {
    const s = this.ticketActivePeriodSummary;
    if (this.activeStatusTab === 'pending') return s.pending;
    if (this.activeStatusTab === 'inProgress') return s.inProgress;
    return s.resolved;
  }

  /** Clean a single raw image token coming from TICKET_IMAGE_PATH */
  private cleanImageToken(token: string): string {
    if (!token) { return ''; }
    let s = token.trim();
    // Strip leading/trailing brackets if present
    if (s.startsWith('[')) {
      s = s.slice(1);
    }
    if (s.endsWith(']')) {
      s = s.slice(0, -1);
    }
    // Strip encoded/trailing quote fragments like %22 or %22,
    if (s.endsWith('%22,')) {
      s = s.slice(0, -4);
    } else if (s.endsWith('%22')) {
      s = s.slice(0, -3);
    }
    // Strip real quotes
    s = s.replace(/^\"|\"$/g, '');
    return s.trim();
  }

  private extractFirstImageUrl(raw: string[] | string | null | undefined): string | undefined {
    const urls = this.parseImageUrls(raw);
    return urls[0];
  }

  /** Parse one or multiple image URLs from TICKET_IMAGE_PATH */
  private parseImageUrls(raw: string[] | string | null | undefined): string[] {
    if (!raw) return [];

    // If backend sends an array (current behaviour)
    if (Array.isArray(raw)) {
      return raw
        .map(x => this.cleanImageToken(x))
        .filter(x => !!x);
    }

    // Fallback: if backend sends a single comma‑separated string
    return raw
      .split(',')
      .map(x => this.cleanImageToken(x))
      .filter(x => !!x);
  }

  private filterTicketsForDialog(tickets: TicketWeb[], status: 'pending' | 'inprogress' | 'resolved'): TicketWeb[] {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(d - 1);

    return tickets.filter(t => {
      const dt = this.parseDate(t.CREATED_ON);
      if (!dt) return false;

      // Date filter aligned with active tab
      if (this.activeDateTab === 'today' && !this.isSameDay(dt, y, m, d)) return false;
      if (this.activeDateTab === 'yesterday' &&
          !this.isSameDay(dt, yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())) return false;
      if (this.activeDateTab === 'last7' && !this.withinLast7Days(dt, now)) return false;
      if (this.activeDateTab === 'month' &&
          !(dt.getFullYear() === y && dt.getMonth() === m)) return false;
      if (this.activeDateTab === 'custom' && !this.withinCustomRange(dt)) return false;

      // Status filter
      const norm = this.normalizeStatus(t.STATUS);
      if (status === 'pending' && norm !== 'pending') return false;
      if (status === 'inprogress' && norm !== 'inProgress') return false;
      if (status === 'resolved' && norm !== 'resolved') return false;

      return true;
    });
  }

  private mapTicketsForDialog(tickets: TicketWeb[]): any[] {
    return tickets.map(t => {
      const dashboardUrls = this.parseImageUrls(t.TICKET_IMAGE_PATH);

      return {
        id: t.ID,
        categoryName: t.CATEGORY_NAME,
        createdOn: t.CREATED_ON,
        campusName: t.CAMPUS_NAME,
        createdBy: t.CREATED_BY,
        createdByDesignation: t.CREATED_BY_DESIGNATION,
        createdByMobile: t.CREATED_BY_MOBILE,
        reason: t.REASON,
        floorName: t.FLOOR_NAME,
        blockName: t.BLOCK_NAME || t.BUILDING_NAME,
        floorNumber: t.FLOOR_NUMBER,
        roomNo: t.ROOM_NO,
        assignedTo: t.ASSIGNED_TO,
        priority: t.PRIORITY,
        status: t.STATUS,
        remarks: t.REMARKS,
        buildingName: t.BUILDING_NAME,
        /** Used by ticket-image-dialog template for [src] binding */
        imageUrls: dashboardUrls,
        ticketImg: dashboardUrls,
        ticketImagePath: dashboardUrls,
      };
    });
  }

  private openTicketDialogIfAny(): void {
    if (!this.ticketDetailList.length) {
      this.snackBar.open('No data found', 'Close', { duration: 3000 });
      return;
    }
    this.dialog.open(TicketImageDialogComponent, {
      data: { tickets: this.ticketDetailList },
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'hygiene-image-dialog'
    });
  }

  openMap(lat: string | number, lng: string | number): void {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
      '_blank'
    );
  }

  /** Grid-level click: find which card was clicked and open dialog (works when Custom Date or child elements receive the click) */
  onPicturesGridClick(e: Event): void {
    const target = e.target as HTMLElement;
    const cardEl = target.closest('.hr-pic-card');
    if (!cardEl) return;
    const idx = cardEl.getAttribute('data-card-index');
    if (idx == null) return;
    const cards = this.ticketBuildingCards;
    const card = cards[+idx];
    if (card) this.onCardClick(card);
  }

  onCardClick(card: { campusId?: number; buildingId?: number; pending: number; inProgress: number; resolved: number }): void {
    if (this.activeDateTab === 'custom' && this.flatpickrInstance) {
      this.flatpickrInstance.close();
    }
    if (card.campusId == null || card.buildingId == null) return;

    let status: 'pending' | 'inprogress' | 'resolved';
    if (this.activeStatusTab === 'pending') {
      status = 'pending';
      if (!card.pending) return;
    } else if (this.activeStatusTab === 'inProgress') {
      status = 'inprogress';
      if (!card.inProgress) return;
    } else {
      status = 'resolved';
      if (!card.resolved) return;
    }
    this.onTicketStatusClick(card, status);
  }

  /** Unique key per card (ticketId so same building can have multiple cards). */
  private cardKey(card: { campusId?: number; buildingId?: number; ticketId?: number }): string | null {
    if (card.ticketId != null) return `ticket_${card.ticketId}`;
    if (card.campusId != null && card.buildingId != null) return `${card.campusId}_${card.buildingId}`;
    return null;
  }

  onCardImageError(card: { campusId?: number; buildingId?: number; ticketId?: number }): void {
    const key = this.cardKey(card);
    if (!key) return;
    this.imageErrorIds.add(key);
  }

  hasCardImageError(card: { campusId?: number; buildingId?: number; ticketId?: number }): boolean {
    const key = this.cardKey(card);
    if (!key) return false;
    return this.imageErrorIds.has(key);
  }

  /** One card per ticket (no grouping) so counts match all tabs and all tickets from response are shown. */
  get ticketBuildingCards(): Array<{ campusName: string; buildingName: string; pending: number; inProgress: number; resolved: number; campusId?: number; buildingId?: number; imageUrl?: string; categoryName?: string; ticketId?: number; createdOn?: string; createdBy?: string; createdByDesignation?: string; createdByMobile?: string; latitude?: number | null; longitude?: number | null }> {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(d - 1);

    const filtered = this.ticketDashboardTickets.filter(t => {
      const dt = this.parseDate(t.CREATED_ON);
      if (!dt) return false;

      if (this.activeDateTab === 'today' && !this.isSameDay(dt, y, m, d)) return false;
      if (this.activeDateTab === 'yesterday' &&
          !this.isSameDay(dt, yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())) return false;
      if (this.activeDateTab === 'last7' && !this.withinLast7Days(dt, now)) return false;
      if (this.activeDateTab === 'month' &&
          !(dt.getFullYear() === y && dt.getMonth() === m)) return false;
      if (this.activeDateTab === 'custom' && !this.withinCustomRange(dt)) return false;

      return true;
    });

    const stFilter = this.activeStatusTab;
    const cards: Array<{
      campusName: string; buildingName: string; pending: number; inProgress: number; resolved: number;
      campusId?: number; buildingId?: number; imageUrl?: string; categoryName?: string; ticketId?: number; createdOn?: string;
      createdBy?: string; createdByDesignation?: string; createdByMobile?: string;
      latitude?: number | null; longitude?: number | null;
    }> = [];

    for (const t of filtered) {
      const st = this.normalizeStatus(t.STATUS);
      if (!st) continue;
      if (stFilter === 'pending' && st !== 'pending') continue;
      if (stFilter === 'inProgress' && st !== 'inProgress') continue;
      if (stFilter === 'resolved' && st !== 'resolved') continue;

      const pending = st === 'pending' ? 1 : 0;
      const inProgress = st === 'inProgress' ? 1 : 0;
      const resolved = st === 'resolved' ? 1 : 0;
      const imageFromTicket = this.extractFirstImageUrl(t.TICKET_IMAGE_PATH);
      cards.push({
        campusName: t.CAMPUS_NAME ?? '',
        buildingName: t.BUILDING_NAME ?? '',
        pending,
        inProgress,
        resolved,
        campusId: Number(t.CAMPUS_ID),
        buildingId: Number(t.BUILDING_ID),
        imageUrl: imageFromTicket,
        categoryName: t.CATEGORY_NAME,
        ticketId: Number(t.ID),
        createdOn: t.CREATED_ON,
        createdBy: t.CREATED_BY,
        createdByDesignation: t.CREATED_BY_DESIGNATION,
        createdByMobile: t.CREATED_BY_MOBILE,
        latitude: t.LATITUDE != null ? Number(t.LATITUDE) : null,
        longitude: t.LONGITUDE != null ? Number(t.LONGITUDE) : null
      });
    }

    return cards;
  }

  ticketDetailList: any[] = [];
  ticketDetailStatus: 'pending' | 'inprogress' | 'resolved' | null = null;

  private currentTypeForDateTab(): 'today' | 'yesterday' | 'last7days' | 'thismonth' {
    switch (this.activeDateTab) {
      case 'today': return 'today';
      case 'yesterday': return 'yesterday';
      case 'last7': return 'last7days';
      case 'month': return 'thismonth';
      default: return 'today';
    }
  }

  onTicketStatusClick(card: { campusId?: number; buildingId?: number; ticketId?: number }, status: 'pending' | 'inprogress' | 'resolved'): void {
    if (!card.campusId || !card.buildingId) {
      this.ticketDetailList = [];
      this.ticketDetailStatus = null;
      return;
    }
    this.ticketDetailStatus = status;

    // Use already-loaded dashboard tickets (same data that built the cards), so date range
    // (Today / Yesterday / Last 7 / Month / Custom) and campus/building match exactly.
    const forCampusBuilding = this.ticketDashboardTickets.filter(
      t => Number(t.CAMPUS_ID) === card.campusId && Number(t.BUILDING_ID) === card.buildingId
    );
    let filtered = this.filterTicketsForDialog(forCampusBuilding, status);

    // One card per ticket: show only the clicked ticket in the dialog (avoid showing #84 when user clicked #86)
    if (card.ticketId != null && filtered.length > 0) {
      const forClickedTicket = filtered.filter(t => Number(t.ID) === card.ticketId);
      if (forClickedTicket.length > 0) filtered = forClickedTicket;
    }

    if (!filtered.length) {
      this.ticketDetailList = [];
      this.snackBar.open('No data found', 'Close', { duration: 3000 });
      return;
    }

    this.ticketDetailList = this.mapTicketsForDialog(filtered);
    this.openTicketDialogIfAny();
  }

  @ViewChild('searchStateInput') searchStateInput!: ElementRef;
  @ViewChild('searchZoneInput') searchZoneInput!: ElementRef;
  @ViewChild('searchCityInput') searchCityInput!: ElementRef;
  @ViewChild('searchCampusInput') searchCampusInput!: ElementRef;
  @ViewChild('searchBuildingInput') searchBuildingInput!: ElementRef;
  @ViewChild('dateRangeInput') dateRangeInput!: ElementRef;
  @ViewChild('customDateWrap') customDateWrap!: ElementRef;
  @ViewChild('customDatePopover') customDatePopover!: ElementRef;

  constructor(
    private hygieneService:HygieneStaftReportService,
    private ticketingService: TicketingService,
    private elRef: ElementRef,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: Event): void {
    const target = e.target as HTMLElement;
    const isTrigger = target.closest('.hr-combobox-trigger');
    const isPanel = target.closest('.hr-combobox-panel');
    if (!isTrigger && !isPanel) {
      this.closeAllDropdowns();
    }
    // Close Custom Date popover when clicking outside trigger and popover
    if (this.activeDateTab === 'custom' && this.customDatePopoverOpen) {
      const isCustomTrigger = target.closest('.hr-custom-date-trigger');
      const isCustomPopover = target.closest('.hr-custom-date-popover');
      const isFlatpickr = target.closest('.flatpickr-calendar');
      if (!isCustomTrigger && !isCustomPopover && !isFlatpickr) {
        this.closeCustomDatePopover();
      }
    }
  }

  ngOnInit(): void {
    this.activeDateTab = 'today';
    this.setDateRangeForTab();
    this.loadStates();
    this.loadTicketDashboard();
    // this.fetchReport();
  }

  private buildDashboardFilterParams(): {
    stateId?: string;
    zoneId?: string;
    cityId?: string;
    campusId?: string;
    buildingId?: string;
  } {
    const params: any = {};
    if (this.selectedStateId != null) params.stateId = String(this.selectedStateId);
    if (this.selectedZoneId != null) params.zoneId = String(this.selectedZoneId);
    if (this.selectedCityId != null) params.cityId = String(this.selectedCityId);
    if (this.selectedCampusId != null) params.campusId = String(this.selectedCampusId);
    if (this.selectedBuildingId != null) params.buildingId = String(this.selectedBuildingId);
    return params;
  }

  /** Unwrap API response: backend may return array or { data: [...] } when using filters. */
  private normalizeTicketDashboardResponse(res: any): TicketWeb[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    const list = res?.data ?? res?.tickets ?? res?.list ?? [];
    return Array.isArray(list) ? list : [];
  }

  loadTicketDashboard(): void {
    const params = this.buildDashboardFilterParams();
    this.spinner.show();
    this.ticketingService.getTicketDashBoardDetailsWithFilters(params).subscribe({
      next: (res: any) => {
        this.ticketDashboardTickets = this.normalizeTicketDashboardResponse(res);
        this.updateDashboardFromTickets();
        this.currentPage = 1;
        this.spinner.hide();
      },
      error: () => {
        this.ticketDashboardTickets = [];
        this.updateDashboardFromTickets();
        this.currentPage = 1;
        this.spinner.hide();
      }
    });
  }

  loadStates(): void {
    this.ticketingService.getStates().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data ?? res?.states ?? res ?? []);
        this.stateList = Array.isArray(list) ? list : [];
        this.filteredStateList = [...this.stateList];
      },
      error: () => { this.stateList = []; this.filteredStateList = []; }
    });
  }

  selectState(s: any): void {
    this.selectedStateId = s?.stateId ?? s?.id ?? null;
    this.selectedStateName = s ? (s.stateName ?? s.name ?? '') : '';
    this.stateQuery = '';
    this.stateOpen = false;

    // Reset downstream
    this.selectedZoneId = null;
    this.selectedZoneName = null;
    this.zoneQuery = '';
    this.zoneList = [];
    this.filteredZoneList = [];

    this.selectedCityId = null;
    this.selectedCityName = '';
    this.cityQuery = '';
    this.cityList = [];
    this.filteredCityList = [];

    this.selectedCampusId = null;
    this.selectedCampusName = '';
    this.campusQuery = '';
    this.campusList = [];
    this.filteredCampusList = [];

    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.buildingQuery = '';
    this.buildingList = [];
    this.filteredBuildingList = [];

    if (this.selectedStateId != null) {
      this.ticketingService.getZonesByStateId(this.selectedStateId).subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : (res?.data ?? res?.zones ?? res ?? []);
          this.zoneList = Array.isArray(list) ? list : [];
          this.filteredZoneList = [...this.zoneList];
        },
        error: () => { this.zoneList = []; this.filteredZoneList = []; }
      });
    }
  }

  selectZone(z: any): void {
    const zoneId = z?.ZONE_ID ?? z?.zoneId ?? z?.id ?? null;
    const zoneName = z?.ZONE_NAME ?? z?.zoneName ?? z?.name ?? z ?? null;
    this.selectedZoneId = zoneId;
    this.selectedZoneName = zoneName;
    this.zoneQuery = '';
    this.zoneOpen = false;

    // Reset downstream
    this.selectedCityId = null;
    this.selectedCityName = '';
    this.cityQuery = '';
    this.cityList = [];
    this.filteredCityList = [];

    this.selectedCampusId = null;
    this.selectedCampusName = '';
    this.campusQuery = '';
    this.campusList = [];
    this.filteredCampusList = [];

    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.buildingQuery = '';
    this.buildingList = [];
    this.filteredBuildingList = [];

    if (this.selectedStateId != null && zoneId != null) {
      this.ticketingService.getCitiesByZone(this.selectedStateId, zoneId).subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : (res?.data ?? res?.cityList ?? res ?? []);
          this.cityList = Array.isArray(list) ? list : [];
          this.filteredCityList = [...this.cityList];
        },
        error: () => { this.cityList = []; this.filteredCityList = []; }
      });
    }
  }

  ngAfterViewChecked(): void {
    if (this.activeDateTab === 'custom' && this.customDatePopoverOpen && this.dateRangeInput?.nativeElement && !this.dateRangePickerInit) {
      this.dateRangePickerInit = true;
      setTimeout(() => this.initDateRangePicker(), 100);
    }
  }

  selectDateTab(tab: 'today' | 'yesterday' | 'last7' | 'month' | 'custom'): void {
    this.activeDateTab = tab;
    this.activeStatusTab = 'pending';
    this.currentPage = 1;
    if (tab !== 'custom') {
      this.closeCustomDatePopover();
      this.dateRangePickerInit = false;
      this.setDateRangeForTab();
    } else {
      this.setDateRangeForTab();
      this.customDatePopoverOpen = true;
      this.openCalendarWhenReady = true;
      this.dateRangePickerInit = false;
      if (!this.customStartDate || !this.customEndDate) {
        this.reportData = null;
      }
    }
  }

  toggleCustomDatePopover(): void {
    this.customDatePopoverOpen = !this.customDatePopoverOpen;
    if (this.customDatePopoverOpen) {
      this.openCalendarWhenReady = true;
      this.dateRangePickerInit = false;
    } else {
      this.closeCustomDatePopover();
    }
  }

  closeCustomDatePopover(): void {
    if (!this.customDatePopoverOpen && !this.flatpickrInstance) return;
    this.customDatePopoverOpen = false;
    if (this.flatpickrInstance) {
      try {
        this.flatpickrInstance.destroy();
      } catch (_) {}
      this.flatpickrInstance = null;
    }
    this.dateRangePickerInit = false;
  }

  selectStatusTab(tab: 'pending' | 'inProgress' | 'resolved'): void {
    this.activeStatusTab = tab;
    this.currentPage = 1;
  }

  /** Compute startDate and endDate based on active tab (for API calls) */
  setDateRangeForTab(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);

    switch (this.activeDateTab) {
      case 'today':
        this.startDate = fmt(today);
        this.endDate = fmt(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        this.startDate = fmt(yesterday);
        this.endDate = fmt(yesterday);
        break;
      case 'last7':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 6);
        this.startDate = fmt(weekAgo);
        this.endDate = fmt(today);
        break;
      case 'month':
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.startDate = fmt(firstOfMonth);
        this.endDate = fmt(today);
        break;
      case 'custom':
        if (this.customStartDate && this.customEndDate) {
          this.startDate = this.customStartDate;
          this.endDate = this.customEndDate;
        } else {
          this.startDate = '';
          this.endDate = '';
        }
        break;
    }
  }

  initDateRangePicker(): void {
    const el = this.dateRangeInput?.nativeElement;
    const appendToEl = this.customDatePopover?.nativeElement ?? this.customDateWrap?.nativeElement;
    if (!el) return;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const options: any = {
      mode: 'range',
      dateFormat: 'Y-m-d',
      maxDate: today,
      onChange: (dates: Date[], _dateStr: string, instance: any) => {
        if (dates.length >= 2) {
          this.customStartDate = this.formatDateToLocalYMD(dates[0]);
          this.customEndDate = this.formatDateToLocalYMD(dates[1]);
          this.startDate = this.customStartDate;
          this.endDate = this.customEndDate;
          instance.set('minDate', undefined);
          instance.set('maxDate', today);
          this.closeCustomDatePopover();
        } else if (dates.length === 1) {
          this.customStartDate = this.formatDateToLocalYMD(dates[0]);
          const start = new Date(dates[0]);
          instance.set('minDate', start);
          instance.set('maxDate', today);
          this.customEndDate = '';
          this.startDate = this.customStartDate;
          this.endDate = '';
        } else {
          this.customStartDate = '';
          this.customEndDate = '';
          instance.set('minDate', undefined);
          instance.set('maxDate', today);
        }
      }
    };
    if (appendToEl) {
      options.appendTo = appendToEl;
    }
    this.flatpickrInstance = flatpickr(el, options);
    if (this.openCalendarWhenReady) {
      this.openCalendarWhenReady = false;
      setTimeout(() => this.flatpickrInstance?.open(), 150);
    }
  }

  /** Set custom date range to today only (quick option when Custom Date tab is active) */
  setCustomRangeToToday(): void {
    const today = new Date();
    const todayStr = this.formatDateToLocalYMD(today);
    this.customStartDate = todayStr;
    this.customEndDate = todayStr;
    this.startDate = todayStr;
    this.endDate = todayStr;
    if (this.flatpickrInstance) {
      this.flatpickrInstance.setDate([todayStr, todayStr], true);
    }
  }

  loadCities(): void {
    this.hygieneService.getEmpCities().subscribe({
      next: (res: any) => {
        this.cityList = Array.isArray(res) ? res : (res?.cityList || res || []);
        this.filteredCityList = [...this.cityList];
      },
      error: () => { this.cityList = []; this.filteredCityList = []; }
    });
  }

  selectCity(c: any): void {
    this.selectedCityId = c?.CITY_ID ?? c?.cityId ?? c?.id ?? null;
    this.selectedCityName = c ? (c.CITY_NAME ?? c.cityName ?? c.name ?? '') : '';
    this.cityQuery = '';
    this.cityOpen = false;
    this.selectedCampusId = null;
    this.selectedCampusName = '';
    this.campusQuery = '';
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.campusList = [];
    this.filteredCampusList = [];
    this.buildingList = [];
    this.filteredBuildingList = [];
    if (c) {
      const id = c?.CITY_ID ?? c?.cityId ?? c?.id;
      this.ticketingService.getCampusesByCity(id).subscribe({
        next: (res: any) => {
          this.campusList = Array.isArray(res) ? res : (res || []);
          this.filteredCampusList = [...this.campusList];
        },
        error: () => { this.campusList = []; this.filteredCampusList = []; }
      });
    }
  }

  selectCampus(c: any): void {
    this.selectedCampusId = c?.CAMPUS_ID ?? c?.campusId ?? c?.id ?? null;
    this.selectedCampusName = c ? (c.CAMPUS_NAME ?? c.campusName ?? c.name ?? '') : '';
    this.campusQuery = '';
    this.campusOpen = false;
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.buildingQuery = '';
    this.buildingList = [];
    this.filteredBuildingList = [];
    if (c) {
      const id = c?.CAMPUS_ID ?? c?.campusId ?? c?.id;
      this.ticketingService.getBuildingsByCampus(id).subscribe({
        next: (res: any) => {
          this.buildingList = Array.isArray(res) ? res : (res || []);
          this.filteredBuildingList = [...this.buildingList];
        },
        error: () => { this.buildingList = []; this.filteredBuildingList = []; }
      });
    }
  }

  selectBuilding(b: any): void {
    this.selectedBuildingId = b?.BUILDING_ID ?? b?.buildingId ?? b?.id ?? null;
    this.selectedBuildingName = b ? (b.BUILDING_NAME ?? b.buildingName ?? b.name ?? '') : '';
    this.buildingQuery = '';
    this.buildingOpen = false;
  }

  closeAllDropdowns(): void {
    this.stateOpen = false;
    this.zoneOpen = false;
    this.cityOpen = false;
    this.campusOpen = false;
    this.buildingOpen = false;
  }

  openState(): void {
    this.closeAllDropdowns();
    this.stateOpen = true;
    this.stateQuery = '';
    this.filterStates();
  }
  openZone(): void {
    this.closeAllDropdowns();
    this.zoneOpen = true;
    this.zoneQuery = '';
    this.filterZones();
  }
  openCity(): void {
    this.closeAllDropdowns();
    this.cityOpen = true;
    this.cityQuery = '';
    this.filterCities();
  }
  openCampus(): void {
    this.closeAllDropdowns();
    this.campusOpen = true;
    this.campusQuery = '';
    this.filterCampuses();
  }
  openBuilding(): void {
    this.closeAllDropdowns();
    this.buildingOpen = true;
    this.buildingQuery = '';
    this.filterBuildings();
  }

  focusCityInput(): void {
    setTimeout(() => this.searchCityInput?.nativeElement.focus(), 200);
  }
  focusCampusInput(): void {
    setTimeout(() => this.searchCampusInput?.nativeElement.focus(), 200);
  }
  focusBuildingInput(): void {
    setTimeout(() => this.searchBuildingInput?.nativeElement.focus(), 200);
  }
  focusStateInput(): void {
    setTimeout(() => this.searchStateInput?.nativeElement.focus(), 200);
  }
  focusZoneInput(): void {
    setTimeout(() => this.searchZoneInput?.nativeElement.focus(), 200);
  }

  filterStates(): void {
    const q = this.stateQuery.toLowerCase();
    this.filteredStateList = this.stateList.filter((s: any) =>
      (s.stateName ?? s.name ?? '').toString().toLowerCase().includes(q)
    );
  }
  filterZones(): void {
    const q = this.zoneQuery.toLowerCase();
    this.filteredZoneList = this.zoneList.filter((z: any) =>
      (z.ZONE_NAME ?? z.zoneName ?? z.name ?? z).toString().toLowerCase().includes(q)
    );
  }
  filterCities(): void {
    const q = this.cityQuery.toLowerCase();
    this.filteredCityList = this.cityList.filter((c: any) =>
      (c.CITY_NAME ?? c.cityName ?? c.name ?? '').toString().toLowerCase().includes(q)
    );
  }
  filterCampuses(): void {
    const q = this.campusQuery.toLowerCase();
    this.filteredCampusList = this.campusList.filter((c: any) =>
      (c.CAMPUS_NAME ?? c.campusName ?? c.name ?? '').toString().toLowerCase().includes(q)
    );
  }
  filterBuildings(): void {
    const q = this.buildingQuery.toLowerCase();
    this.filteredBuildingList = this.buildingList.filter((b: any) =>
      (b.BUILDING_NAME ?? b.buildingName ?? b.name ?? '').toString().toLowerCase().includes(q)
    );
  }

  // fetchReport(): void {
  //   this.setDateRangeForTab();
  //   if (this.activeDateTab === 'custom' && (!this.startDate || !this.endDate)) {
  //     this.reportData = null;
  //     return;
  //   }
  //   const filterTypeMap: Record<string, string> = { today: 'TODAY', yesterday: 'YESTERDAY', last7: 'LAST7DAYS', month: 'THISMONTH' };
  //   const params: any = {};
  //   if (this.selectedCityId) params.cityId = String(this.selectedCityId);
  //   if (this.selectedCampusId) params.campusId = String(this.selectedCampusId);
  //   if (this.selectedBuildingId) params.buildingId = String(this.selectedBuildingId);
  //   if (this.activeDateTab === 'custom') {
  //     params.startDate = this.startDate;
  //     params.endDate = this.endDate;
  //   } else {
  //     params.filterType = filterTypeMap[this.activeDateTab] ?? 'TODAY';
  //   }
  //   this.imageErrorIds.clear();
  //   this.spinner.show();
  //   this.hygieneService.getStaffReport(params).subscribe({
  //     next: (res: any) => {
  //       this.reportData = this.mapReportResponse(res);
  //       this.currentPage = 1;
  //       this.spinner.hide();
  //     },
  //     error: () => {
  //       this.reportData = null;
  //       this.currentPage = 1;
  //       this.spinner.hide();
  //     }
  //   });
  // }

  onSearch(): void {
    // this.fetchReport();
    this.loadTicketDashboard();
  }

  setPage(page: number): void {
    const p = Math.max(1, Math.min(page, this.totalPages));
    if (p !== this.currentPage) this.currentPage = p;
  }

  /** Map API response to HygieneStaffReport (handles wrapped/nested structures) */
  mapReportResponse(res: any): HygieneStaffReport {
    const data = res?.data ?? res;
    return {
      todayPhotoCount: data?.todayPhotoCount ?? 0,
      yesterdayPhotoCount: data?.yesterdayPhotoCount ?? 0,
      last7DaysPhotoCount: data?.last7DaysPhotoCount ?? 0,
      thisMonthPhotoCount: data?.thisMonthPhotoCount ?? 0,
      pictures: Array.isArray(data?.pictures) ? data.pictures : []
    };
  }

  /** Format hygieneDate "07-03-2026 00:00:00" to display time or date */
  onImageError(_imageRefId: number): void {
    // Kept for template compatibility; ticket images for hygiene use a separate mechanism.
  }

  openImageDialog(p: any): void {
    this.dialog.open(TicketImageDialogComponent, {
      data: { picture: p },
      width: '900px',
      maxWidth: '95vw',
      minWidth: '320px',
      panelClass: 'hygiene-image-dialog'
    });
  }

  formatHygieneTime(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split(' ');
    if (parts.length >= 2 && parts[1] !== '00:00:00') {
      const [h, m, s] = parts[1].split(':');
      const hh = parseInt(h || '0', 10);
      const ampm = hh >= 12 ? 'PM' : 'AM';
      const h12 = hh % 12 || 12;
      return `${String(h12).padStart(2, '0')}:${m}:${s} ${ampm}`;
    }
    return parts[0] || dateStr;
  }
}
